import { useEffect, useState, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';
import { useUpdateLink, useUploadAvatar, type UpdateLinkBody } from '../../lib/hooks.ts';
import { ApiError } from '../../lib/api.ts';
import type { TrackingLink } from '../../lib/types.ts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  link: TrackingLink;
}

export function EditTrackingLinkDialog({ open, onOpenChange, link }: Props): JSX.Element {
  const [form, setForm] = useState({
    source: link.source,
    destination_url: link.destinationUrl,
    channel_title: link.channelTitle ?? '',
    channel_avatar_url: link.channelAvatarUrl ?? '',
    channel_description: link.channelDescription ?? '',
    cta_label: link.ctaLabel ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const update = useUpdateLink();
  const uploadAvatar = useUploadAvatar();

  async function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    try {
      const { url } = await uploadAvatar.mutateAsync(file);
      setForm((f) => ({ ...f, channel_avatar_url: url }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        setUploadError('Файл слишком большой. Лимит 2 МБ.');
      } else if (err instanceof ApiError && err.status === 422) {
        setUploadError('Неподдерживаемый формат. Только JPEG, PNG, WebP.');
      } else {
        setUploadError(err instanceof Error ? err.message : 'Не удалось загрузить файл');
      }
    }
  }

  // Pre-fill сбрасывается на свежие данные при каждом открытии диалога —
  // иначе после отмены и повторного клика в state останутся старые правки.
  useEffect(() => {
    if (open) {
      setForm({
        source: link.source,
        destination_url: link.destinationUrl,
        channel_title: link.channelTitle ?? '',
        channel_avatar_url: link.channelAvatarUrl ?? '',
        channel_description: link.channelDescription ?? '',
        cta_label: link.ctaLabel ?? '',
      });
      setError(null);
    }
  }, [open, link]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    // Пустую channel-строку шлём как null чтобы можно было обнулить поле.
    // source и destination_url непустые по UI required → шлём как строку.
    const patch: UpdateLinkBody = {
      source: form.source.trim(),
      destination_url: form.destination_url.trim(),
      channel_title: form.channel_title.trim() || null,
      channel_avatar_url: form.channel_avatar_url.trim() || null,
      channel_description: form.channel_description.trim() || null,
      // Пустая строка → null: владелец стёр кастомный текст → возврат к дефолту.
      cta_label: form.cta_label.trim() || null,
    };

    try {
      await update.mutateAsync({ id: link.id, patch });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError('Проверь поля: URL — полный с https://.');
      } else if (err instanceof ApiError && err.status === 404) {
        setError('Ссылка не найдена. Возможно, её удалили.');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось сохранить изменения');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={onSubmit}>
          <DialogTitle>Редактировать ссылку</DialogTitle>
          <DialogDescription>
            Slug изменить нельзя — он используется в публичном URL. Остальные поля можно править.
          </DialogDescription>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-link-slug">Идентификатор</Label>
              <Input
                id="edit-link-slug"
                value={link.slug}
                disabled
                className="font-mono opacity-60"
              />
              <p className="text-xs text-fg-muted">Slug фиксируется при создании.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-link-source">Источник</Label>
              <Input
                id="edit-link-source"
                value={form.source}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-link-destination">Куда отправлять (Telegram URL)</Label>
              <Input
                id="edit-link-destination"
                value={form.destination_url}
                onChange={(e) => setForm((f) => ({ ...f, destination_url: e.target.value }))}
                required
                type="url"
                maxLength={2000}
              />
            </div>

            <div className="sm:col-span-2 mt-2 border-t border-border pt-3">
              <p className="mb-2 text-xs text-fg-muted">
                Channel-preview для лендинга (опционально).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-link-channel-title">Название канала</Label>
              <Input
                id="edit-link-channel-title"
                value={form.channel_title}
                onChange={(e) => setForm((f) => ({ ...f, channel_title: e.target.value }))}
                placeholder="Self-Worth Channel"
                maxLength={200}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-link-channel-avatar">Аватарка</Label>
              <div className="flex items-center gap-2">
                {form.channel_avatar_url ? (
                  <img
                    src={form.channel_avatar_url}
                    alt="preview"
                    className="h-10 w-10 rounded-full border border-border object-cover"
                  />
                ) : null}
                <label
                  htmlFor="edit-link-channel-avatar-file"
                  className="btn-secondary cursor-pointer text-xs"
                >
                  {uploadAvatar.isPending
                    ? 'Загружаем…'
                    : form.channel_avatar_url
                      ? 'Заменить файлом'
                      : 'Выбрать файл'}
                </label>
                <input
                  id="edit-link-channel-avatar-file"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => void onAvatarChange(e)}
                />
              </div>
              <Input
                id="edit-link-channel-avatar"
                value={form.channel_avatar_url}
                onChange={(e) => setForm((f) => ({ ...f, channel_avatar_url: e.target.value }))}
                placeholder="https://cdn.example.com/avatar.png (или загрузи файл выше)"
                type="url"
                maxLength={2000}
              />
              {uploadError ? <p className="text-xs text-danger">{uploadError}</p> : null}
              <p className="text-xs text-fg-muted">
                JPEG, PNG или WebP до 2 МБ. URL подставится автоматически после загрузки.
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-link-channel-description">Описание канала</Label>
              <Input
                id="edit-link-channel-description"
                value={form.channel_description}
                onChange={(e) => setForm((f) => ({ ...f, channel_description: e.target.value }))}
                placeholder="Daily reflections about self-worth"
                maxLength={2000}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-link-cta-label">Текст кнопки</Label>
              <Input
                id="edit-link-cta-label"
                value={form.cta_label}
                onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
                placeholder="Вступить в Telegram"
                maxLength={50}
              />
              <p className="text-xs text-fg-muted">
                Пусто — вернётся дефолт «Вступить в Telegram». До 50 символов.
              </p>
            </div>
          </div>

          {error ? (
            <p className="mt-3 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="primary" disabled={update.isPending}>
              {update.isPending ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
